// src/layouts/MainLayout.jsx

import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppNotifications } from '../context/AppNotificationsContext';
import { getNavItemsForRole } from '../config/navigation';

const ICONS = {
  dashboard: 'M4 4h7v7H4V4zm9 0h7v4h-7V4zm0 7h7v9h-7v-9zM4 14h7v6H4v-6z',
  sales: 'M3 17l6-6 4 4 8-8M21 7v6h-6',
  commission: 'M12 1v22M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6',
  contracts: 'M9 2h6l5 5v13a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2h3zM9 2v6h6',
  expenses: 'M12 1v22M5 6h14M5 18h14',
  payroll: 'M3 7h18M3 12h18M3 17h18',
  employees: 'M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
  onboarding: 'M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM19 8l2 2 4-4',
  attendance: 'M12 8v4l3 3M12 2a10 10 0 110 20 10 10 0 010-20z',
  leave: 'M8 7V3m8 4V3M3 11h18M5 5h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z',
  assets: 'M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z',
  bank: 'M3 21h18M4 10h16M12 3l9 5H3l9-5zM6 10v9M10 10v9M14 10v9M18 10v9',
  performance: 'M18 20V10M12 20V4M6 20v-6',
  reports: 'M3 3v18h18M7 14l4-4 4 4 4-8',
  audit: 'M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11',
  selfservice: 'M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z',
  settings: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008.6 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H2a2 2 0 110-4h.09A1.65 1.65 0 003.6 8.6a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06a1.65 1.65 0 001.82.33H8a1.65 1.65 0 001-1.51V2a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V8a1.65 1.65 0 001.51 1H22a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z',
  offboarding: 'M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zm9-3l5 5m0-5l-5 5',
  bell: 'M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0',
  department: 'M3 21h18M3 9l9-7 9 7M3 9v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9M9 9v12m6-12v12',
  invoice: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM14 2v6h6M10 13h4M10 17h4M8 13h.01M8 17h.01',
  calendar: 'M8 7V3m8 4V3M3 11h18M5 5h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z',
  schedules: 'M8 7V3m8 4V3M3 11h18M5 5h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z',
};

// ── Each module gets its own brand accent color (full safe classnames, no dynamic strings) ──
const ICON_COLORS = {
  dashboard:   { text: 'text-violet-600',  hoverText: 'group-hover:text-violet-600',  bg: 'bg-violet-50',   activeBg: 'bg-violet-100',   dot: 'bg-violet-600' },
  sales:       { text: 'text-blue-600',    hoverText: 'group-hover:text-blue-600',    bg: 'bg-blue-50',     activeBg: 'bg-blue-100',     dot: 'bg-blue-600' },
  commission:  { text: 'text-amber-600',   hoverText: 'group-hover:text-amber-600',   bg: 'bg-amber-50',    activeBg: 'bg-amber-100',    dot: 'bg-amber-600' },
  contracts:   { text: 'text-indigo-600',  hoverText: 'group-hover:text-indigo-600',  bg: 'bg-indigo-50',   activeBg: 'bg-indigo-100',   dot: 'bg-indigo-600' },
  invoice:     { text: 'text-cyan-600',    hoverText: 'group-hover:text-cyan-600',    bg: 'bg-cyan-50',     activeBg: 'bg-cyan-100',     dot: 'bg-cyan-600' },
  expenses:    { text: 'text-orange-600',  hoverText: 'group-hover:text-orange-600',  bg: 'bg-orange-50',   activeBg: 'bg-orange-100',   dot: 'bg-orange-600' },
  payroll:     { text: 'text-emerald-600', hoverText: 'group-hover:text-emerald-600', bg: 'bg-emerald-50',  activeBg: 'bg-emerald-100',  dot: 'bg-emerald-600' },
  employees:   { text: 'text-pink-600',    hoverText: 'group-hover:text-pink-600',    bg: 'bg-pink-50',     activeBg: 'bg-pink-100',     dot: 'bg-pink-600' },
  department:  { text: 'text-teal-600',    hoverText: 'group-hover:text-teal-600',    bg: 'bg-teal-50',     activeBg: 'bg-teal-100',     dot: 'bg-teal-600' },
  onboarding:  { text: 'text-lime-600',    hoverText: 'group-hover:text-lime-600',    bg: 'bg-lime-50',     activeBg: 'bg-lime-100',     dot: 'bg-lime-600' },
  offboarding: { text: 'text-rose-600',    hoverText: 'group-hover:text-rose-600',    bg: 'bg-rose-50',     activeBg: 'bg-rose-100',     dot: 'bg-rose-600' },
  schedules:   { text: 'text-sky-600',     hoverText: 'group-hover:text-sky-600',     bg: 'bg-sky-50',      activeBg: 'bg-sky-100',      dot: 'bg-sky-600' },
  bank:        { text: 'text-violet-700',  hoverText: 'group-hover:text-violet-700',  bg: 'bg-violet-50',   activeBg: 'bg-violet-100',   dot: 'bg-violet-700' },
  calendar:    { text: 'text-sky-600',     hoverText: 'group-hover:text-sky-600',     bg: 'bg-sky-50',      activeBg: 'bg-sky-100',      dot: 'bg-sky-600' },
  attendance:  { text: 'text-fuchsia-600', hoverText: 'group-hover:text-fuchsia-600', bg: 'bg-fuchsia-50',  activeBg: 'bg-fuchsia-100',  dot: 'bg-fuchsia-600' },
  leave:       { text: 'text-red-600',     hoverText: 'group-hover:text-red-600',     bg: 'bg-red-50',      activeBg: 'bg-red-100',      dot: 'bg-red-600' },
  assets:      { text: 'text-purple-600',  hoverText: 'group-hover:text-purple-600',  bg: 'bg-purple-50',   activeBg: 'bg-purple-100',   dot: 'bg-purple-600' },
  performance: { text: 'text-green-600',   hoverText: 'group-hover:text-green-600',   bg: 'bg-green-50',    activeBg: 'bg-green-100',    dot: 'bg-green-600' },
  reports:     { text: 'text-yellow-600',  hoverText: 'group-hover:text-yellow-600',  bg: 'bg-yellow-50',   activeBg: 'bg-yellow-100',   dot: 'bg-yellow-600' },
  audit:       { text: 'text-stone-600',   hoverText: 'group-hover:text-stone-600',   bg: 'bg-stone-100',   activeBg: 'bg-stone-200',    dot: 'bg-stone-600' },
  selfservice: { text: 'text-blue-600',    hoverText: 'group-hover:text-blue-600',    bg: 'bg-blue-50',     activeBg: 'bg-blue-100',     dot: 'bg-blue-600' },
  settings:    { text: 'text-slate-600',   hoverText: 'group-hover:text-slate-600',   bg: 'bg-slate-100',   activeBg: 'bg-slate-200',    dot: 'bg-slate-600' },
};

const getIconColor = (name) =>
  ICON_COLORS[name] || {
    text: 'text-gray-600',
    hoverText: 'group-hover:text-gray-600',
    bg: 'bg-gray-50',
    activeBg: 'bg-gray-100',
    dot: 'bg-gray-600',
  };

const Icon = ({ name, className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d={ICONS[name] || ICONS.dashboard} />
  </svg>
);

const roleLabels = {
  super_admin: 'CEO / Super Admin',
  hr_manager: 'HR Manager',
  finance_manager: 'Finance Manager',
  sales_manager: 'Sales Manager',
  sales_rep: 'Sales Representative',
  employee: 'Employee',
};

const MainLayout = () => {
  const { user, logout } = useAuth();
  const { unreadCount } = useAppNotifications();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = user ? getNavItemsForRole(user.role) : [];

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const initials = (user?.full_name || user?.employee_id || '?')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="flex h-screen bg-surface-subtle overflow-hidden">

      {/* ── Mobile backdrop ── */}
      {mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
        />
      )}

      {/* ── Sidebar ── */}
      <aside
        className={
          'fixed inset-y-0 left-0 z-40 w-72 transform transition-transform duration-200 ' +
          (mobileOpen ? 'translate-x-0' : '-translate-x-full') +
          ' lg:static lg:translate-x-0 lg:z-auto ' +
          (collapsed ? 'lg:w-[72px]' : 'lg:w-64') +
          ' bg-white border-r border-gray-100 flex flex-col'
        }
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100 shrink-0">
          {!collapsed && (
            <span className="ml-2.5 font-bold text-gray-900 text-sm whitespace-nowrap tracking-tight">
              SYNVEX MANAGEMENT
            </span>
          )}
          <button onClick={() => setMobileOpen(false)} className="lg:hidden text-gray-400 hover:text-gray-600 p-1">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
              <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {navItems.map((item) => {
            const color = getIconColor(item.icon);
            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  'group relative flex items-center gap-3 px-2.5 py-2 rounded-control text-sm font-medium transition-all ' +
                  (isActive
                    ? `bg-primary/[0.06] ${color.text}`
                    : `text-gray-500 hover:bg-surface-subtle ${color.hoverText}`)
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={
                        'flex items-center justify-center w-8 h-8 rounded-xl shrink-0 transition-all duration-200 ' +
                        (isActive
                          ? `${color.activeBg} shadow-sm ring-1 ring-black/[0.03] scale-105`
                          : `${color.bg} group-hover:${color.activeBg} group-hover:scale-105`)
                      }
                    >
                      <Icon
                        name={item.icon}
                        className={'w-[17px] h-[17px] ' + color.text}
                      />
                    </span>
                    {!collapsed && (
                      <span className="whitespace-nowrap transition-colors flex-1">
                        {item.label}
                      </span>
                    )}
                    {item.path === '/notifications' && unreadCount > 0 && !collapsed && (
                      <span className="ml-auto bg-primary text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                    {item.path === '/notifications' && unreadCount > 0 && collapsed && (
                      <span className="absolute top-1 right-1.5 w-2 h-2 rounded-full bg-primary" />
                    )}
                    {isActive && !collapsed && item.path !== '/notifications' && (
                      <span className={'ml-auto w-1.5 h-1.5 rounded-full ' + color.dot} />
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <button
          onClick={() => setCollapsed((c) => !c)}
          className="hidden lg:flex h-12 items-center justify-center text-gray-400 hover:text-gray-600 border-t border-gray-100 shrink-0"
        >
          <span className="text-xs">{collapsed ? '»' : '« Collapse'}</span>
        </button>
      </aside>

      {/* ── Main column ── */}
      <div className="flex-1 flex flex-col overflow-hidden">

        <header className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-4 sm:px-6 shrink-0 gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700 shrink-0 p-1"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
                <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
              </svg>
            </button>
            <input
              type="text"
              placeholder="Search across modules..."
              className="hidden sm:block w-full max-w-md px-3.5 py-2 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light focus:border-primary-light transition-shadow"
            />
          </div>

          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            <button
              className="relative text-gray-400 hover:text-gray-600"
              onClick={() => navigate('/notifications')}
              title={unreadCount > 0 ? unreadCount + ' unread notification' + (unreadCount > 1 ? 's' : '') : 'Notifications'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 01-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <div className="relative">
              <button
                onClick={() => setProfileOpen((p) => !p)}
                className="flex items-center gap-2.5"
              >
                <div className="w-8 h-8 rounded-full bg-primary-light/15 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                  {initials}
                </div>
                <div className="hidden sm:block text-left">
                  <p className="text-sm font-medium text-gray-800 leading-tight">
                    {user?.full_name || user?.employee_id}
                  </p>
                  <p className="text-xs text-gray-400 leading-tight">
                    {roleLabels[user?.role] || user?.role}
                  </p>
                </div>
              </button>

              {profileOpen && (
                <div className="absolute right-0 mt-2 w-44 bg-white rounded-control shadow-elevated border border-gray-100 py-1 z-10">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-white">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default MainLayout;